import { ServerIntegrityCard } from '@/components/integrity/ServerIntegrityCard';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, ShieldCheck, ShieldAlert, ShieldQuestion, Clock } from 'lucide-react';
import {
  deriveIntegrityState, INTEGRITY_CHECK_FAMILIES, preventedRuleCount,
  type IntegrityRunRow, type IntegrityViolationRow, type IntegrityState,
} from '@/utils/operationalIntegrity';
import { remediationFor, type InvariantRuleId } from '@/utils/invariantCoverage';

/**
 * CORE-BQ Closure Slice B — the shop-facing integrity surface (item 2).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS PAGE REFUSES TO DO
 * ═══════════════════════════════════════════════════════════════════════════
 * It never shows a reassuring state because it found no violations. Every
 * headline comes from `deriveIntegrityState`, which reads the RUN — so "audited
 * and clean", "audited but unfinished", "never audited" and "the write failed"
 * are four visibly different answers rather than one green tick.
 *
 * Deliberately small: current state, what was found, when it was checked, and a
 * short history. No filtering, no charting, no remediation, no navigation
 * redesign. The frozen requirement asks for current violations and a trend, and
 * that is the whole of it.
 */

const STATE_STYLE: Record<IntegrityState, {
  label: string; icon: typeof ShieldCheck; className: string;
}> = {
  HEALTHY: { label: 'Consistent', icon: ShieldCheck, className: 'text-emerald-600' },
  VIOLATIONS_FOUND: { label: 'Contradictions found', icon: ShieldAlert, className: 'text-destructive' },
  AUDIT_INCOMPLETE: { label: 'Audit incomplete', icon: ShieldQuestion, className: 'text-amber-600' },
  STALE: { label: 'Out of date', icon: Clock, className: 'text-amber-600' },
  NEVER_AUDITED: { label: 'Not audited', icon: ShieldQuestion, className: 'text-muted-foreground' },
};

function whenText(d: Date | null): string {
  if (!d) return 'never';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export default function OperationalIntegrity() {
  const { activeShop } = useAuth();
  const shopId = activeShop?.id;

  // Recent runs, newest first. One query serves both the current state (the
  // first row) and the trend (all of them), so the headline and the history can
  // never disagree about which audit is the latest.
  const runsQuery = useQuery({
    queryKey: ['integrity-runs', shopId],
    enabled: !!shopId,
    queryFn: async (): Promise<IntegrityRunRow[]> => {
      const { data, error } = await supabase
        .from('integrity_audit_runs')
        .select('id, verdict, coverage_complete, violation_count, rules_checked, rules_unevaluated, checked_at, entry_point, source')
        .eq('shop_id', shopId!)
        .eq('entry_point', 'schedule')
        .order('checked_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as IntegrityRunRow[];
    },
  });

  const latest = runsQuery.data?.[0] ?? null;

  const violationsQuery = useQuery({
    queryKey: ['integrity-violations', latest?.id],
    enabled: !!latest?.id,
    queryFn: async (): Promise<IntegrityViolationRow[]> => {
      const { data, error } = await supabase
        .from('integrity_violations')
        .select('id, rule, severity, message, record_kind, record_id, fields, details, observed_at')
        .eq('run_id', latest!.id)
        .order('severity', { ascending: true });
      if (error) throw error;
      return (data ?? []) as IntegrityViolationRow[];
    },
  });

  if (!shopId) {
    return <div className="p-6 text-muted-foreground">Select a shop to view its integrity.</div>;
  }

  if (runsQuery.isLoading) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading integrity results…
      </div>
    );
  }

  if (runsQuery.isError) {
    // A failed read is NOT an absence of problems. Saying so is the same rule
    // the rest of this capability runs on.
    return (
      <div className="p-6">
        <Card className="border-amber-500/40">
          <CardHeader>
            <CardTitle className="text-amber-600">Integrity results could not be loaded</CardTitle>
            <CardDescription>
              This is a failure to read the record, not a statement about the board.
              Nothing here should be taken as evidence that the shop is consistent.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const status = deriveIntegrityState(latest);
  const style = STATE_STYLE[status.state];
  const Icon = style.icon;
  const violations = violationsQuery.data ?? [];

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold">Operational integrity</h1>
        <p className="text-muted-foreground text-sm">
          Whether this shop's operational picture is internally possible.
        </p>
      </div>

      {/* Current state. The headline is derived, never inferred from an empty list. */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <Icon className={`h-6 w-6 mt-0.5 ${style.className}`} aria-hidden />
            <div>
              <CardTitle className={style.className}>{style.label}</CardTitle>
              <CardDescription className="mt-1">{status.headline}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-1">
          <div>Last checked: {whenText(status.checkedAt)}</div>
          {latest && (
            <div>
              {latest.rules_checked} invariant{latest.rules_checked === 1 ? '' : 's'} checked
              {latest.rules_unevaluated > 0 && `, ${latest.rules_unevaluated} not evaluated`}
              {' · '}covered {latest.coverage_complete ? 'completely' : 'partially'}
            </div>
          )}
          {status.isStale && status.state !== 'STALE' && (
            <div className="text-amber-600">
              This result is older than a day and may not describe the board as it stands now.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Core-100 R2 CORE-BQ: the run the server authored, beside the browser-authored audits. */}
      <ServerIntegrityCard shopId={shopId} />

      {/* Current violations. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Current contradictions</CardTitle>
          <CardDescription>
            {status.state === 'NEVER_AUDITED'
              ? 'Nothing to show — no audit has been recorded.'
              : `From the audit ${whenText(status.checkedAt)}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {violationsQuery.isLoading && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!violationsQuery.isLoading && violations.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {status.state === 'HEALTHY'
                ? 'None. Every invariant that ran was satisfied.'
                : 'None recorded by the latest audit — which, given the state above, is not the same as none existing.'}
            </p>
          )}
          <ul className="space-y-3">
            {violations.map((v) => (
              <li key={v.id} className="border rounded-md p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={v.severity === 'error' ? 'destructive' : 'secondary'}>
                    {v.severity}
                  </Badge>
                  <span className="font-mono text-xs">{v.rule}</span>
                  {v.record_id && (
                    <span className="text-xs text-muted-foreground">
                      {v.record_kind ?? 'record'} {v.record_id}
                    </span>
                  )}
                </div>
                <p className="text-sm mt-2">{v.message}</p>
                {v.fields && v.fields.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Fields read by this rule: {v.fields.join(', ')}
                  </p>
                )}
                {/* CORE-BQ Slice D (Definition of Done item 7) — remediation,
                    proposed and nothing else.

                    The wording below is guidance a person reads and acts on.
                    OptiBay does not carry it out: there is no path from a
                    violation to a write, and this slice deliberately did not
                    add one. Saying so on the card matters — advice that looks
                    like it might already have been applied is worse than no
                    advice, because the reader stops checking. */}
                <div className="mt-2 rounded-md bg-muted/50 border p-2">
                  <div className="text-xs font-medium">Suggested next step</div>
                  <p className="text-sm mt-0.5">{remediationFor(v.rule as InvariantRuleId)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    OptiBay has not changed anything. This is a suggestion for someone to act on.
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* CORE-BQ Slice C (Definition of Done item 9) — the adoption path.

          A shop should be able to understand what this catches BEFORE it has
          findings to read, and before deciding anything. The families are built
          from the invariant registry, so this cannot promise a check that no
          longer exists or quietly omit one that was added. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">What this checks</CardTitle>
          <CardDescription>
            OptiBay runs these automatically whenever work is placed on the board.
            You do not switch this on, and checking never changes anything —
            letting OptiBay act on its own is a separate setting in Shop Profile.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="space-y-3">
            {INTEGRITY_CHECK_FAMILIES.map((f) => (
              <li key={f.title}>
                <div className="font-medium text-sm">{f.title}</div>
                <p className="text-sm text-muted-foreground">{f.description}</p>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground border-t pt-3">
            {preventedRuleCount()} of these the database refuses outright, so the
            contradiction cannot be written at all — by this app, by an import, or
            by a background job. The rest are detected and reported here.
          </p>
        </CardContent>
      </Card>

      {/* Trend. Deliberately a list, not a chart — the frozen requirement is
          that a shop can see whether integrity is improving or degrading, and
          twenty dated results answer that without a charting dependency. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent audits</CardTitle>
          <CardDescription>Whether integrity is improving or degrading.</CardDescription>
        </CardHeader>
        <CardContent>
          {(runsQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No audits recorded yet.</p>
          ) : (
            <ul className="text-sm divide-y">
              {(runsQuery.data ?? []).map((r) => (
                <li key={r.id} className="py-2 flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">
                    {new Date(r.checked_at).toLocaleString()}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-muted-foreground text-xs">
                      {r.violation_count} found
                    </span>
                    <Badge
                      variant={r.verdict === 'FAIL' ? 'destructive'
                        : r.verdict === 'PASS' ? 'secondary' : 'outline'}
                    >
                      {r.verdict === 'INCOMPLETE' ? 'incomplete' : r.verdict.toLowerCase()}
                    </Badge>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
